import { App as AntdApp, message as staticMessage } from "antd";
import { useEffect, useId, useRef, type ReactNode } from "react";
import { Alert as UiKitAlert, type AlertProps as UiKitAlertProps } from "zhihao-ui-react";
import { hasRecentGlobalRequestError } from "@/utils/requestErrorState";

/** 兼容迁移页面的 Alert 属性，同时为全局消息保留 message/title 两种旧文案写法。 */
export type PageAlertProps = Omit<UiKitAlertProps, "title"> & {
    message?: ReactNode;
    title?: ReactNode;
};

/** 页面反馈统一使用旧版 Vue ElMessage 的全局消息，避免错误提示挤占页面内容。 */
export const PageAlert = ({
    message: alertMessage,
    title,
    description,
    type = "info",
    action,
    ...props
}: PageAlertProps) => {
    const appMessage = AntdApp.useApp().message;
    // 测试或独立挂载场景可能没有 AntdApp，生产页面则使用根节点提供的上下文消息实例。
    const messageApi = typeof appMessage.open === "function" ? appMessage : staticMessage;
    const alertTitle = title ?? alertMessage;
    // Ant Alert 允许仅传 description；UI Kit 需将其放入标题位，避免出现组件默认占位标题。
    const hasDescriptionOnly = alertTitle == null && description != null;
    const content = hasDescriptionOnly ? description : alertTitle;
    const messageKey = useId();
    const signature = `${type}|${String(content ?? "")}|${String(description ?? "")}|${Boolean(action)}`;
    const lastSignature = useRef<string | undefined>(undefined);
    const shouldNotify = type === "error" || type === "success" || props.role === "alert";

    useEffect(() => {
        if (!shouldNotify || content == null) {
            messageApi.destroy(messageKey);
            return;
        }
        // 请求层已经展示后端具体原因时，任何页面级兜底错误都会重复提示；统一只保留请求层的全局消息。
        const duplicateRequestFailure = type === "error" && hasRecentGlobalRequestError();
        if (duplicateRequestFailure) {
            messageApi.destroy(messageKey);
            return;
        }
        if (lastSignature.current === signature && !action) return;
        lastSignature.current = signature;

        const messageType: "info" | "success" | "warning" | "error" =
            type === "primary" ? "info" : type === "info" || type === "success" || type === "warning" ? type : "error";
        const messageContent = (
            <span>
                {content}
                {description != null && !hasDescriptionOnly ? <span>{description}</span> : null}
                {action ? <span onClick={() => messageApi.destroy(messageKey)}>{action}</span> : null}
            </span>
        );
        messageApi.open({
            key: messageKey,
            type: messageType,
            content: messageContent,
            duration: action ? 0 : 3
        });
    }, [action, content, description, hasDescriptionOnly, messageApi, messageKey, shouldNotify, signature, type]);

    useEffect(
        () => () => {
            // React StrictMode 会在开发环境先清理再重放 effect；清理时重置签名，避免第二次 effect 被错误去重。
            lastSignature.current = undefined;
            messageApi.destroy(messageKey);
        },
        [messageApi, messageKey]
    );

    // 页面只保留无视觉语义节点，避免原有错误状态测试和读屏用户失去反馈。
    if (shouldNotify) {
        return content == null ? null : (
            <div
                className="zh-alert page-alert-message-a11y"
                role="alert"
                style={{
                    position: "absolute",
                    width: 1,
                    height: 1,
                    padding: 0,
                    margin: -1,
                    overflow: "hidden",
                    clip: "rect(0, 0, 0, 0)",
                    whiteSpace: "nowrap",
                    border: 0
                }}
            >
                {content}
                {description != null && !hasDescriptionOnly ? description : null}
            </div>
        );
    }

    return (
        <UiKitAlert
            {...props}
            title={hasDescriptionOnly ? description : alertTitle}
            description={hasDescriptionOnly ? undefined : description}
            type={type}
        />
    );
};
